import * as React from 'react';
import { useMemo } from 'react';

import { Search as SearchIcon } from '@mui/icons-material';
import {
  Autocomplete,
  InputAdornment,
  TextField,
  createFilterOptions,
} from '@mui/material';

import styles from './ComparisonPicker.module.css';
import { splitByMatch } from './splitByMatch';

export interface ComparisonPickerOption<V extends string | number> {
  value: V;
  label: string;
}

interface Props<V extends string | number> {
  id: string;
  label: string;
  placeholder: string;
  helperText: string;
  noOptionsText: string;
  options: ComparisonPickerOption<V>[];
  value: V | null | undefined;
  onChange: (value: V) => void;
  disabled?: boolean;
}

function ComparisonPicker<V extends string | number>({
  id,
  label,
  placeholder,
  helperText,
  noOptionsText,
  options,
  value,
  onChange,
  disabled = false,
}: Props<V>) {
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );
  const filterOptions = useMemo(
    () => createFilterOptions<ComparisonPickerOption<V>>({ trim: true }),
    [],
  );

  return (
    <Autocomplete<ComparisonPickerOption<V>, false, true, false>
      id={id}
      fullWidth
      disableClearable
      openOnFocus
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      disabled={disabled}
      options={options}
      filterOptions={filterOptions}
      // disableClearable types the value as non-null, but nothing is selected
      // until the user picks an option.
      value={selectedOption as ComparisonPickerOption<V>}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      onChange={(_event, option) => onChange(option.value)}
      noOptionsText={noOptionsText}
      renderOption={(optionProps, option, { inputValue }) => {
        // When the dropdown first opens the input still shows the selected
        // label, so nothing has been typed yet and nothing should be bold.
        const query = inputValue === selectedOption?.label ? '' : inputValue;
        return (
          <li {...optionProps} key={String(option.value)}>
            <span>
              {splitByMatch(option.label, query).map((part, index) =>
                part.match ? (
                  <mark key={index} className={styles.match}>
                    {part.text}
                  </mark>
                ) : (
                  <React.Fragment key={index}>{part.text}</React.Fragment>
                ),
              )}
            </span>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label={label}
          placeholder={placeholder}
          // A disabled picker has nothing to search; keep the line so the
          // layout doesn't shift.
          helperText={disabled ? ' ' : helperText}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
}

export default ComparisonPicker;
